import { composeGraphQLSelections, graphql, type AnyGraphQLSelection } from "../remote/graphql-document"
import { defineGraphQLQuery } from "../remote/graphql-query"
import type { RemoteClient } from "../remote/remote-client"
import type { Session } from "../session/session"
import type { ApplicationPlugin, ApplicationPluginAvailability } from "./composition"

interface ApplicationAuthenticatedRootResult {
  readonly useCases: Record<string, unknown>
}

export type ApplicationAuthenticatedState =
  | {
      readonly status: "loading"
      readonly accessContextRevision: number
    }
  | {
      readonly status: "unauthenticated"
      readonly accessContextRevision: number
    }
  | {
      readonly status: "ready"
      readonly accessContextRevision: number
    }
  | {
      readonly status: "failed"
      readonly accessContextRevision: number
      readonly error: unknown
    }

interface ApplicationAuthenticatedRootRuntime {
  readonly state: ApplicationAuthenticatedState
  subscribe(listener: () => void): () => void
  whenAuthenticated(): Promise<void>
  retry(): Promise<void>
  getPluginAvailability(plugin: ApplicationPlugin | string): ApplicationPluginAvailability | null
  isPluginAvailable(plugin: ApplicationPlugin | string): boolean
  requiresAuthenticatedRoot(plugin: ApplicationPlugin | string): boolean
}

/** Builds the authenticated Root/useCases query from selection blocks. */
function createAuthenticatedRootQuery(selections: readonly AnyGraphQLSelection[]) {
  if (selections.length === 0) return null

  const useCases = composeGraphQLSelections(...selections)
  const document = graphql<ApplicationAuthenticatedRootResult>`
    query ApplicationAuthenticatedRoot {
      useCases {
        ${useCases}
      }
    }
  `

  return defineGraphQLQuery({
    endpoint: "Root",
    document,
  })
}

export function createApplicationAuthenticatedRootRuntime(
  plugins: readonly ApplicationPlugin[],
  session: Session,
  remote: RemoteClient
): ApplicationAuthenticatedRootRuntime {
  const pluginsById = new Map(plugins.map((plugin) => [plugin.id, plugin]))
  const availability = new Map<string, ApplicationPluginAvailability>()
  const listeners = new Set<() => void>()
  const hasAuthenticatedRootPlugins = plugins.some(hasAuthenticatedRootLifecycle)
  let state: ApplicationAuthenticatedState = Object.freeze({
    status:
      hasAuthenticatedRootPlugins && session.status === "signed-in"
        ? "loading"
        : session.status === "signed-in"
          ? "ready"
          : "unauthenticated",
    accessContextRevision: session.accessContext.revision,
  })
  let generation = 0
  let currentPromise: Promise<void> | null = null
  /** The access context whose loaded availability waits for the same user to log in again. */
  let suspendedAccessContextRevision: number | null = null

  const getPlugin = (pluginOrId: ApplicationPlugin | string) => {
    const id = typeof pluginOrId === "string" ? pluginOrId : pluginOrId.id
    const plugin = pluginsById.get(id)
    if (plugin == null) throw new Error(`Application plugin is not registered: ${id}.`)
    return plugin
  }

  const setState = (next: ApplicationAuthenticatedState) => {
    state = Object.freeze(next)
    for (const listener of listeners) listener()
  }

  const clearAvailability = () => availability.clear()

  const isCurrent = (expectedGeneration: number, expectedAccessContextRevision: number) =>
    expectedGeneration === generation &&
    session.status === "signed-in" &&
    session.accessContext.revision === expectedAccessContextRevision

  const requiresAuthenticatedRoot = (pluginOrId: ApplicationPlugin | string): boolean => {
    const plugin = getPlugin(pluginOrId)
    if (hasAuthenticatedRootLifecycle(plugin)) return true
    return plugin.parentId == null ? false : requiresAuthenticatedRoot(plugin.parentId)
  }

  const getPluginAvailability = (pluginOrId: ApplicationPlugin | string): ApplicationPluginAvailability | null => {
    const plugin = getPlugin(pluginOrId)
    if (!hasAuthenticatedRootLifecycle(plugin)) return null
    return availability.get(plugin.id) ?? null
  }

  const isPluginAvailable = (pluginOrId: ApplicationPlugin | string) => {
    const plugin = getPlugin(pluginOrId)
    if (!hasAuthenticatedRootLifecycle(plugin)) return true
    if (session.status !== "signed-in") return false
    return getPluginAvailability(plugin)?.isAvailable === true
  }

  async function loadPluginAvailability(expectedGeneration: number, expectedAccessContextRevision: number) {
    const selections = new Map(plugins.map((plugin) => [plugin.id, plugin.onAuthenticatedRootQuery?.()]))
    const query = createAuthenticatedRootQuery(
      [...selections.values()].filter((selection): selection is AnyGraphQLSelection => selection != null)
    )
    const useCases = query == null ? {} : (await query.create().execute(remote)).useCases
    if (!isCurrent(expectedGeneration, expectedAccessContextRevision)) return null

    const next = new Map<string, ApplicationPluginAvailability>()

    for (const plugin of plugins) {
      if (!hasAuthenticatedRootLifecycle(plugin)) continue
      if (!isCurrent(expectedGeneration, expectedAccessContextRevision)) return null

      const mapping = selections.get(plugin.id)?.result
      const result = await plugin.onAuthenticatedRootQueryResult?.(
        mapping === undefined ? useCases : mapping.decode(useCases)
      )
      if (!isCurrent(expectedGeneration, expectedAccessContextRevision)) return null
      next.set(
        plugin.id,
        Object.freeze({
          isAvailable: result?.isAvailable ?? true,
          ...(result?.featureId == null ? {} : { featureId: result.featureId }),
        })
      )
    }

    return next
  }

  function startLoad(accessContextRevision: number) {
    const currentGeneration = ++generation
    clearAvailability()
    setState({ status: "loading", accessContextRevision })

    const promise = loadPluginAvailability(currentGeneration, accessContextRevision).then(
      (next) => {
        if (next == null || !isCurrent(currentGeneration, accessContextRevision)) return

        for (const [pluginId, value] of next) availability.set(pluginId, value)
        setState({ status: "ready", accessContextRevision })
      },
      (error) => {
        if (!isCurrent(currentGeneration, accessContextRevision)) return

        setState({ status: "failed", accessContextRevision, error })
        throw error
      }
    )
    currentPromise = promise
    void promise.catch(() => undefined)
  }

  function reconcile() {
    const accessContextRevision = session.accessContext.revision

    if (!hasAuthenticatedRootPlugins) {
      const status = session.status === "signed-in" ? "ready" : "unauthenticated"
      if (state.status !== status || state.accessContextRevision !== accessContextRevision) {
        ++generation
        currentPromise = null
        setState({ status, accessContextRevision })
      }
      return
    }

    if (!session.isRestored) {
      const status = session.status === "signed-in" ? "loading" : "unauthenticated"
      if (state.status !== status || state.accessContextRevision !== accessContextRevision) {
        ++generation
        currentPromise = null
        clearAvailability()
        setState({ status, accessContextRevision })
      }
      return
    }

    if (session.status !== "signed-in") {
      if (
        session.status === "authentication-required" &&
        state.status === "ready" &&
        state.accessContextRevision === accessContextRevision
      ) {
        // An expired authorization keeps its access context. Availability stays loaded for a resumed login,
        // while isPluginAvailable still answers false until the session is signed in again.
        suspendedAccessContextRevision = accessContextRevision
        ++generation
        currentPromise = null
        setState({ status: "unauthenticated", accessContextRevision })
        return
      }
      if (
        suspendedAccessContextRevision != null &&
        (session.status !== "authentication-required" || suspendedAccessContextRevision !== accessContextRevision)
      ) {
        suspendedAccessContextRevision = null
        clearAvailability()
      }
      if (state.status !== "unauthenticated" || state.accessContextRevision !== accessContextRevision) {
        suspendedAccessContextRevision = null
        ++generation
        currentPromise = null
        clearAvailability()
        setState({ status: "unauthenticated", accessContextRevision })
      }
      return
    }

    if (suspendedAccessContextRevision != null) {
      const resumed = suspendedAccessContextRevision === accessContextRevision && state.status === "unauthenticated"
      suspendedAccessContextRevision = null
      if (resumed) {
        // Reloading would unmount the protected content that the resumed login keeps.
        setState({ status: "ready", accessContextRevision })
        return
      }
    }

    if (state.accessContextRevision === accessContextRevision) {
      if (state.status === "ready" || state.status === "failed") return
      if (state.status === "loading" && currentPromise != null) return
    }

    startLoad(accessContextRevision)
  }

  async function whenAuthenticated() {
    if (!hasAuthenticatedRootPlugins) return
    if (!session.isRestored) await session.whenRestored()

    while (true) {
      reconcile()
      if (session.status !== "signed-in") return

      const accessContextRevision = session.accessContext.revision
      const promise = currentPromise
      if (promise != null) await promise

      if (session.status !== "signed-in") return
      if (session.accessContext.revision !== accessContextRevision) continue
      if (state.status === "ready") return
      if (state.status === "failed") throw state.error
    }
  }

  async function retry() {
    if (!hasAuthenticatedRootPlugins) return
    if (!session.isRestored) await session.whenRestored()

    reconcile()
    if (session.status !== "signed-in") return

    const accessContextRevision = session.accessContext.revision
    if (state.status === "failed" && state.accessContextRevision === accessContextRevision) {
      startLoad(accessContextRevision)
    }

    await whenAuthenticated()
  }

  session.subscribe(reconcile)
  session.subscribeAccessContext(reconcile)
  void session.whenRestored().then(reconcile)

  return Object.freeze({
    get state() {
      return state
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    whenAuthenticated,
    retry,
    getPluginAvailability,
    isPluginAvailable,
    requiresAuthenticatedRoot,
  })
}

function hasAuthenticatedRootLifecycle(plugin: ApplicationPlugin) {
  return plugin.onAuthenticatedRootQuery != null || plugin.onAuthenticatedRootQueryResult != null
}
